import { afterNextRender, type ElementRef, type Injector, signal } from '@angular/core';
import { createKeyboardNavigator, isHandledKey, type DocumentItem, type KeyIntent } from '@document-explorer/core';

export interface RovingHandlers {
  onOpen(item: DocumentItem): void;
  onToggleSelect(item: DocumentItem): void;
  onSelectRange(item: DocumentItem): void;
  onSelectAll(): void;
  onNavigateUp(): void;
  onClearSelection(): void;
}

/**
 * Roving tabindex plus key handling for the Angular views.
 *
 * The contract — which key means what — comes from
 * `createKeyboardNavigator()` in `@document-explorer/core`, the same function
 * the React package uses. Only the focus mechanics differ between frameworks.
 */
export class RovingFocus {
  private readonly navigator = createKeyboardNavigator();
  private readonly index = signal(0);
  /** Whether the user was working inside this list when it last changed. */
  private hasFocus = false;

  readonly activeIndex = this.index.asReadonly();

  constructor(
    private readonly container: () => ElementRef<HTMLElement> | undefined,
    private readonly injector?: Injector,
  ) {}

  tabIndexFor(index: number): number {
    return index === this.index() ? 0 : -1;
  }

  /**
   * Track focus at the container rather than per row, so a row unmounting
   * mid-navigation does not look like the user leaving the list.
   */
  noteFocusIn(): void {
    this.hasFocus = true;
  }

  noteFocusOut(event: FocusEvent): void {
    const next = event.relatedTarget as Node | null;
    const host = this.container()?.nativeElement;
    // A row being removed blurs with no relatedTarget. That is focus we should
    // reclaim; focus the user deliberately moved elsewhere is not.
    if (next && host) this.hasFocus = host.contains(next);
  }

  /**
   * Point the roving index at a freshly rendered list.
   *
   * A new list — a folder was opened, the query changed, the sort flipped —
   * starts at the top, because the old index would land on an unrelated row.
   * Opening a folder also removes the focused row, and the browser drops focus
   * to <body>, stranding a keyboard user mid-navigation with no way back in
   * except tabbing from the top. Reclaim it, but only if the list genuinely had
   * focus, or the explorer would steal it on first render.
   */
  syncToList(): void {
    // Nothing reactive may be *read* here. This runs inside an effect, so a
    // read of the index signal would make the effect depend on it — and every
    // arrow key would then re-run this and reset the index it just moved.
    this.index.set(0);
    if (!this.hasFocus) return;

    // Angular runs effects during change detection, *before* this component's
    // view is refreshed: at this point the old rows are still in the DOM and
    // still hold focus, so checking now would always conclude there is nothing
    // to reclaim. The reclaim has to wait for the new rows to exist.
    const reclaim = () => {
      const host = this.container()?.nativeElement;
      if (!host || host.contains(host.ownerDocument.activeElement)) return;
      this.focusActive();
    };

    // `afterNextRender` is tied to the render itself, so focus lands before the
    // user's next key can arrive. A microtask only *usually* wins that race —
    // it flushes before the render when change detection is scheduled on a
    // frame, which showed up as a rare lost keystroke. The fallback covers a
    // RovingFocus built without an injector.
    if (this.injector) afterNextRender(reclaim, { injector: this.injector });
    else queueMicrotask(reclaim);
  }

  setActive(index: number, focus = false): void {
    this.index.set(index);
    if (focus) this.focusActive();
  }

  handleKey(
    event: KeyboardEvent,
    items: readonly DocumentItem[],
    handlers: RovingHandlers,
    columns = 1,
  ): void {
    const intent: KeyIntent = this.navigator.resolve(
      event,
      { items, activeIndex: this.index(), columns },
      Date.now(),
    );
    if (!intent) return;
    if (isHandledKey(event) || intent.type === 'move') event.preventDefault();

    switch (intent.type) {
      case 'move': {
        this.setActive(intent.index, true);
        const target = items[intent.index];
        if (intent.extend && target) handlers.onSelectRange(target);
        return;
      }
      case 'open':
        return handlers.onOpen(intent.item);
      case 'toggleSelect':
        return handlers.onToggleSelect(intent.item);
      case 'selectAll':
        return handlers.onSelectAll();
      case 'navigateUp':
        return handlers.onNavigateUp();
      case 'clearSelection':
        return handlers.onClearSelection();
    }
  }

  private focusActive(): void {
    const host = this.container()?.nativeElement;
    const target = host?.querySelector<HTMLElement>(`[data-de-index="${this.index()}"]`);
    target?.focus();
    // Guarded: jsdom and some older environments do not implement this.
    target?.scrollIntoView?.({ block: 'nearest' });
  }
}
